/**
 * useGoodsReceipts Hook
 *
 * React Query hooks for goods receipts.
 * @see US-9.4 - Page Liste Receptions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  goodsReceiptsService,
  type QueryGoodsReceiptsDto,
  type CreateGoodsReceiptDto,
  type UpdateGoodsReceiptDto,
  type UpdateGoodsReceiptItemDto,
  type InitReceiptFromOrderDto,
} from '../services/goods-receipts.service';

/**
 * Query key factory for goods receipts
 */
export const receiptKeys = {
  all: ['goods-receipts'] as const,
  lists: () => [...receiptKeys.all, 'list'] as const,
  list: (params: QueryGoodsReceiptsDto) => [...receiptKeys.lists(), params] as const,
  details: () => [...receiptKeys.all, 'detail'] as const,
  detail: (id: string) => [...receiptKeys.details(), id] as const,
  kpis: (startDate?: string, endDate?: string) => [...receiptKeys.all, 'kpis', startDate, endDate] as const,
};

/**
 * Hook to fetch goods receipts list with pagination
 */
export function useGoodsReceipts(params: QueryGoodsReceiptsDto) {
  return useQuery({
    queryKey: receiptKeys.list(params),
    queryFn: () => goodsReceiptsService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch goods receipts KPIs
 */
export function useGoodsReceiptsKPIs(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: receiptKeys.kpis(startDate, endDate),
    queryFn: () => goodsReceiptsService.getKPIs(startDate, endDate),
  });
}

/**
 * Hook to fetch single goods receipt
 */
export function useGoodsReceipt(id: string | null) {
  return useQuery({
    queryKey: receiptKeys.detail(id!),
    queryFn: () => goodsReceiptsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a goods receipt
 */
export function useCreateGoodsReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, files }: { data: CreateGoodsReceiptDto; files?: { blFiles?: File[]; invoiceFiles?: File[] } }) =>
      goodsReceiptsService.create(data, files),
    onSuccess: (receipt) => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      toast.success(`Bon de reception ${receipt.receiptNumber} cree`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a goods receipt
 */
export function useUpdateGoodsReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGoodsReceiptDto }) =>
      goodsReceiptsService.update(id, data),
    onSuccess: (receipt) => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      toast.success(`Bon de reception ${receipt.receiptNumber} mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to delete a goods receipt
 */
export function useDeleteGoodsReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => goodsReceiptsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      toast.success('Bon de reception supprime');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to update an item in a goods receipt
 */
export function useUpdateGoodsReceiptItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      receiptId,
      itemId,
      data,
    }: {
      receiptId: string;
      itemId: string;
      data: UpdateGoodsReceiptItemDto;
    }) => goodsReceiptsService.updateItem(receiptId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      toast.success('Ligne mise a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to remove an item from a goods receipt
 */
export function useRemoveGoodsReceiptItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ receiptId, itemId }: { receiptId: string; itemId: string }) =>
      goodsReceiptsService.removeItem(receiptId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      toast.success('Ligne supprimee');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to validate a goods receipt
 */
export function useValidateGoodsReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => goodsReceiptsService.validate(id),
    onSuccess: (receipt) => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      // Also invalidate purchase orders as their status may have changed
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success(`Bon de reception ${receipt.receiptNumber} valide`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la validation');
    },
  });
}

/**
 * Hook to cancel a goods receipt
 */
export function useCancelGoodsReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => goodsReceiptsService.cancel(id),
    onSuccess: (receipt) => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      // Also invalidate purchase orders as their status may have changed
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast.success(`Bon de reception ${receipt.receiptNumber} annule`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'annulation");
    },
  });
}

/**
 * Hook to generate PDF bon de reception (SuperAdmin backfill)
 */
export function useGenerateGoodsReceiptPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => goodsReceiptsService.generatePdf(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      toast.success('PDF bon de reception genere');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la generation du PDF');
    },
  });
}

/**
 * Hook to init receipt from purchase order
 */
export function useInitReceiptFromOrder() {
  return useMutation({
    mutationFn: (data: InitReceiptFromOrderDto) => goodsReceiptsService.initFromOrder(data),
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la preparation');
    },
  });
}
